<script setup lang="ts">
import { ref, onBeforeMount, watch, toValue } from 'vue';
import { useRoute } from 'vue-router';
import { useLayout } from '@/layout/composables/layout';
import { UserInfo } from '@/modules/user/UserInfo';
import { useUserInfo } from '@/modules/user/useUserInfo';

interface Props {
  item: object;
  index: number;
  root?: boolean;
  parentItemKey?: string | null;
}
const props = withDefaults(defineProps<Props>(), {
  item: () => ({}),
  index: 0,
  root: true,
  parentItemKey: null
});

const route = useRoute();
const userInfo = useUserInfo();
const { layoutConfig, layoutState, setActiveMenuItem, onMenuToggle } = useLayout();
const currentUser = ref<UserInfo>();
const isActiveMenu = ref(false);
const itemKey = ref(null);

onBeforeMount(() => {
  userInfo.getUserInfo().then((userInfo) => {
    currentUser.value = userInfo;
  });
  itemKey.value = props.parentItemKey ? props.parentItemKey + '-' + props.index : String(props.index);

  const activeItem = layoutState.activeMenuItem;

  isActiveMenu.value = activeItem === itemKey.value || activeItem ? activeItem.startsWith(itemKey.value + '-') : false;
});

watch(
  () => layoutConfig.activeMenuItem.value,
  (newVal) => {
    isActiveMenu.value = newVal === itemKey.value || newVal.startsWith(itemKey.value + '-');
  }
);
const itemClick = (event, item) => {
  if (item.disabled) {
    event?.preventDefault();
    return;
  }

  const { overlayMenuActive, staticMenuMobileActive } = layoutState;

  if (((typeof item.to === 'string' && item.to) || (typeof item.to === 'object' && item.to[0]) || item.url) && (staticMenuMobileActive.value || overlayMenuActive.value)) {
    onMenuToggle();
  }

  if (item.command) {
    item.command({ originalEvent: event, item: item });
  }

  let foundItemKey;
  if (item.items) {
    if (isActiveMenu.value) {
      foundItemKey = props.parentItemKey;
    } else {
      foundItemKey = itemKey;
    }
  } else {
    foundItemKey = itemKey.value;
  }

  setActiveMenuItem(foundItemKey);
};

const isRoleValid = (it) => {
  return it.roleNotValid?.indexOf(toValue(currentUser)?.role) === -1;
};

const checkActiveRoute = (item) => {
  if (item.activeRoute) {
    return item.activeRoute.includes(route.name);
  }
  return item.to.name === route.name;
};
</script>

<template>
  <div :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }">
    <div v-if="root && isRoleValid(item)" class="layout-menuitem-root-text">
      {{ item.label }}
    </div>
    <a v-if="isRoleValid(item) && !item.to" @click="itemClick($event, item, index)" :class="item.class" :target="item.target" tabindex="0">
      <i :class="item.icon" class="layout-menuitem-icon"></i>
      <span class="layout-menuitem-text pl-2">{{ item.label }}</span>
      <i class="pi pi-angle-down layout-submenu-toggler" v-if="item.items"></i>
    </a>
    <router-link v-if="isRoleValid(item) && item.to" @click="itemClick($event, item, index)" :class="[item.class, { 'active-route': checkActiveRoute(item) }]" tabindex="0" :to="item.to">
      <i :class="item.icon" class="layout-menuitem-icon"></i>
      <span class="layout-menuitem-text pl-2">{{ item.label }}</span>
      <i class="pi pi-angle-down layout-submenu-toggler" v-if="item.items"></i>
    </router-link>
    <Transition v-if="item.items && isRoleValid(item)" name="layout-submenu">
      <ul v-show="root ? true : isActiveMenu" class="layout-submenu">
        <app-menu-item v-for="(child, i) in item.items" :key="child" :index="i" :item="child" :parentItemKey="itemKey" :root="false"></app-menu-item>
      </ul>
    </Transition>
  </div>
</template>
